import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Form as AntdForm } from 'antd';
import { required } from '../../../../antd/form/FormValidation';
import PhdFormBuilder from '../PhdFormBuilder';
import PhdHelpHint from '../../../../common/Phd/PhdHelpHint.jsx';
import {
  STUDY_FORM_OPTION_KEYS,
  translateOptions,
} from '../phdFormOptions.jsx';
import { createSchoolYearChangeHandler } from '../utils/usePhdSchoolYearOptions';
import {
  getPhdDocumentDependencyState,
  PHD_DOCUMENT_DEPENDENCY_TYPES,
} from '../utils/phdDocumentDependencies.jsx';

const PhdDissertationDefenseExaminerProposalsEditForm = ({
  studentOptions,
  students,
  schoolYearOptions,
  examinerOptions,
  studyFieldLabelOptions,
  ensureSchoolYearSelection,
  isCreatingSchoolYear,
  isSubmitting,
  applicationOptions,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const form = AntdForm.useFormInstance();
  const selectedStudentId = AntdForm.useWatch('phd_student_id', form);
  const selectedSchoolYearId = AntdForm.useWatch('phd_school_year_id', form);
  const handleSchoolYearChange = createSchoolYearChangeHandler(
    form,
    ensureSchoolYearSelection,
  );
  const handleApplicationChange = (applicationId) => {
    const application = (applicationOptions || []).find(
      (option) => Number(option.value) === Number(applicationId),
    );
    const source = application?.application;
    if (source) {
      form.setFieldsValue({
        phd_student_id: source.phd_student_id,
        phd_school_year_id: source.phd_school_year_id,
      });
    }
  };
  const studyFormOptions = useMemo(
    () => translateOptions(STUDY_FORM_OPTION_KEYS, t),
    [t],
  );
  const selectedStudent = useMemo(
    () =>
      (students || []).find(
        (student) => Number(student.id) === Number(selectedStudentId),
      ),
    [selectedStudentId, students],
  );
  const dependencyState = useMemo(
    () =>
      getPhdDocumentDependencyState({
        documentType:
          PHD_DOCUMENT_DEPENDENCY_TYPES.DISSERTATION_DEFENSE_EXAMINER_PROPOSAL,
        student: selectedStudent,
        schoolYearId: selectedSchoolYearId,
      }),
    [selectedSchoolYearId, selectedStudent],
  );

  const examinerField = (name, label) => ({
    name,
    label,
    type: 'select',
    options: examinerOptions,
    colSpan: 8,
  });

  const sections = useMemo(
    () => [
      {
        key: 'basic',
        title: t('Základné informácie'),
        fields: [
          {
            name: 'phd_dissertation_defense_application_id',
            label: (
              <span>
                {t('Prihláška na obhajobu')}
                <PhdHelpHint
                  label={t('Čo znamená nadradená prihláška?')}
                  title={t(
                    'Návrh musí patriť jednej odovzdanej prihláške. Doktorand a školský rok sa načítajú zo zvolenej prihlášky a nemožno ich určiť samostatne. Prihlášky, ktoré už majú priradený návrh (od iného záznamu), sú v zozname zobrazené, ale nedajú sa vybrať – najprv treba odovzdať novú prihlášku.',
                  )}
                />
              </span>
            ),
            type: 'select',
            options: applicationOptions,
            componentProps: { onChange: handleApplicationChange },
            rules: [required(t)],
            colSpan: 24,
          },
          {
            name: 'phd_student_id',
            label: t('Študent'),
            type: 'select',
            options: studentOptions,
            rules: [required(t)],
            componentProps: { disabled: true },
            colSpan: 12,
          },
          {
            name: 'phd_school_year_id',
            label: t('Školský rok'),
            type: 'select',
            options: schoolYearOptions,
            componentProps: {
              disabled: true,
              onChange: handleSchoolYearChange,
              loading: isCreatingSchoolYear,
            },
            rules: [required(t)],
            colSpan: 12,
          },
          examinerField('prof_opponent_id', t('Profesor oponent')),
          examinerField('qualified_opponent_id', t('Kvalifikovaný oponent')),
          examinerField(
            'qualified_opponent_2_id',
            t('Kvalifikovaný oponent 2'),
          ),
          {
            name: 'credits_count',
            label: t('Počet kreditov'),
            type: 'number',
            componentProps: { min: 0, max: 999, style: { width: '100%' } },
            colSpan: 8,
          },
          {
            name: 'application_year',
            label: t('Rok podania'),
            type: 'number',
            componentProps: { min: 1990, max: 2100, style: { width: '100%' } },
            colSpan: 8,
          },
        ],
      },
      {
        key: 'displayNames',
        title: t('Zobrazené mená'),
        fields: [
          {
            name: 'student_display_name',
            label: t('Študent'),
            type: 'text',
            colSpan: 12,
          },
          {
            name: 'supervisor_display_name',
            label: t('Školiteľ'),
            type: 'text',
            colSpan: 12,
          },
          {
            name: 'prof_opponent_display_name',
            label: t('Profesor oponent'),
            type: 'text',
            colSpan: 12,
          },
          {
            name: 'qualified_opponent_display_name',
            label: t('Kvalifikovaný oponent'),
            type: 'text',
            colSpan: 12,
          },
          {
            name: 'qualified_opponent_2_display_name',
            label: t('Kvalifikovaný oponent 2'),
            type: 'text',
            colSpan: 12,
          },
        ],
      },
      {
        key: 'student',
        title: t('Údaje o študentovi'),
        fields: [
          {
            name: 'student_study_form',
            label: t('Forma štúdia'),
            type: 'select',
            options: studyFormOptions,
            colSpan: 12,
          },
          {
            name: 'student_study_field',
            label: t('Študijný odbor'),
            type: 'text',
            colSpan: 12,
          },
          {
            name: 'student_study_program',
            label: t('Študijný program'),
            type: 'select',
            options: studyFieldLabelOptions,
            colSpan: 24,
          },
        ],
      },
    ],
    [
      t,
      studentOptions,
      applicationOptions,
      schoolYearOptions,
      examinerOptions,
      studyFieldLabelOptions,
      studyFormOptions,
      handleSchoolYearChange,
      isCreatingSchoolYear,
    ],
  );

  return (
    <PhdFormBuilder
      sections={sections}
      submitKey="phd-dissertation-defense-examiner-proposals"
      isSubmitting={isSubmitting}
      isSubmitDisabled={dependencyState.missingItems.length > 0}
      hideSubmit={isReadOnly}
      beforeSubmitContent={
        dependencyState.message ? (
          <Alert
            type="warning"
            showIcon
            message={t('Chýbajúce podklady')}
            description={dependencyState.message}
            style={{ marginTop: 16 }}
          />
        ) : null
      }
    />
  );
};

export default PhdDissertationDefenseExaminerProposalsEditForm;
